/**
 * Copilot Panel Component
 *
 * AI-powered chat sidebar for asking questions about procurement data.
 * Provides a collapsible panel interface with conversation management.
 */

import { t } from '../../i18n/translations.js';
import { toast } from '../../services/toast.js';
import copilotService from '../../services/copilot.js';

// State
let state = {
  isOpen: false,
  isLoading: false,
  showHistory: false,
  conversations: [],
  currentConversation: null,
  messages: [],
  inputValue: '',
};

// DOM Elements
let panelEl = null;
let messagesEl = null;
let inputEl = null;

/**
 * Initialize the copilot panel
 */
export function init() {
  injectStyles();
  render();
  attachEventListeners();
}

/**
 * Toggle the panel open/closed
 */
export function toggle() {
  state.isOpen = !state.isOpen;
  updatePanelVisibility();

  if (state.isOpen && state.conversations.length === 0) {
    loadConversations();
  }
}

/**
 * Open the panel
 */
export function open() {
  state.isOpen = true;
  updatePanelVisibility();

  if (state.conversations.length === 0) {
    loadConversations();
  }
}

/**
 * Close the panel
 */
export function close() {
  state.isOpen = false;
  updatePanelVisibility();
}

/**
 * Update panel visibility in DOM
 */
function updatePanelVisibility() {
  if (panelEl) {
    panelEl.classList.toggle('copilot-panel--open', state.isOpen);
  }
}

/**
 * Load user's conversations
 */
async function loadConversations() {
  try {
    const response = await copilotService.listConversations({ pageSize: 50 });
    if (response.success) {
      state.conversations = response.data || [];
    }
  } catch (error) {
    console.error('Failed to load conversations:', error);
  }
}

/**
 * Start a new conversation
 */
async function startNewConversation() {
  state.currentConversation = null;
  state.messages = [];
  state.showHistory = false;
  renderMessages();
}

/**
 * Load a conversation
 */
async function loadConversation(conversationId) {
  state.isLoading = true;
  renderMessages();

  try {
    const response = await copilotService.getConversation(conversationId);
    if (response.success) {
      state.currentConversation = response.data;
      state.messages = response.data.messages || [];
      state.showHistory = false;
    }
  } catch (error) {
    console.error('Failed to load conversation:', error);
    toast.error(t('copilot.error'));
  } finally {
    state.isLoading = false;
    renderMessages();
    scrollToBottom();
  }
}

/**
 * Delete a conversation
 */
async function deleteConversation(conversationId) {
  if (!confirm(t('copilot.deleteConfirm'))) return;

  try {
    await copilotService.deleteConversation(conversationId);
    state.conversations = state.conversations.filter(c => c.id !== conversationId);

    if (state.currentConversation?.id === conversationId) {
      state.currentConversation = null;
      state.messages = [];
    }

    renderHistory();
  } catch (error) {
    console.error('Failed to delete conversation:', error);
    toast.error(t('copilot.error'));
  }
}

/**
 * Send a message
 */
async function sendMessage() {
  const message = state.inputValue.trim();
  if (!message || state.isLoading) return;

  state.inputValue = '';
  if (inputEl) inputEl.value = '';

  // Add user message to UI immediately
  const userMessage = {
    id: `temp-${Date.now()}`,
    role: 'user',
    content: message,
    timestamp: new Date().toISOString(),
  };
  state.messages.push(userMessage);
  renderMessages();
  scrollToBottom();

  // Show loading state
  state.isLoading = true;
  renderMessages();

  try {
    let response;

    if (state.currentConversation) {
      // Send to existing conversation
      response = await copilotService.sendMessage(
        state.currentConversation.id,
        message
      );
    } else {
      // Create new conversation with initial message
      response = await copilotService.quickStart(message);
      if (response.success) {
        state.currentConversation = {
          id: response.data.id,
          title: response.data.title,
        };
        // Add to conversations list
        state.conversations.unshift({
          id: response.data.id,
          title: response.data.title,
          messageCount: 2,
          lastMessageAt: new Date().toISOString(),
        });
      }
    }

    if (response.success) {
      // Replace temp message and add assistant response
      state.messages = state.messages.filter(m => m.id !== userMessage.id);

      if (response.data.userMessage) {
        state.messages.push(response.data.userMessage);
      } else {
        state.messages.push(userMessage);
      }

      if (response.data.assistantMessage) {
        state.messages.push(response.data.assistantMessage);
      }

      // Update conversation title if provided
      if (response.data.title && state.currentConversation) {
        state.currentConversation.title = response.data.title;
        const conv = state.conversations.find(c => c.id === state.currentConversation.id);
        if (conv) conv.title = response.data.title;
      }
    }
  } catch (error) {
    console.error('Failed to send message:', error);
    // Add error message
    state.messages.push({
      id: `error-${Date.now()}`,
      role: 'assistant',
      content: t('copilot.error'),
      timestamp: new Date().toISOString(),
      isError: true,
    });
  } finally {
    state.isLoading = false;
    renderMessages();
    scrollToBottom();
  }
}

/**
 * Handle input keypress
 */
function handleKeyPress(e) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    sendMessage();
  }
}

/**
 * Handle input change
 */
function handleInputChange(e) {
  state.inputValue = e.target.value;
}

/**
 * Toggle history view
 */
function toggleHistory() {
  state.showHistory = !state.showHistory;
  renderMessages();
}

/**
 * Scroll messages to bottom
 */
function scrollToBottom() {
  if (messagesEl) {
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }
}

/**
 * Format timestamp
 */
function formatTime(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Format date for history
 */
function formatDate(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return t('common.today') || 'Today';
  if (diffDays === 1) return t('common.yesterday') || 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'long' });
  return date.toLocaleDateString();
}

/**
 * Render the panel
 */
function render() {
  const container = document.getElementById('copilotPanel');
  if (!container) {
    console.warn('Copilot panel container not found');
    return;
  }

  container.innerHTML = `
    <div class="copilot-panel ${state.isOpen ? 'copilot-panel--open' : ''}">
      <!-- Toggle Button -->
      <button class="copilot-toggle" id="copilotToggle" title="${t('copilot.title')}">
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          <circle cx="12" cy="10" r="1" fill="currentColor"/>
          <circle cx="8" cy="10" r="1" fill="currentColor"/>
          <circle cx="16" cy="10" r="1" fill="currentColor"/>
        </svg>
      </button>

      <!-- Panel Content -->
      <div class="copilot-content">
        <!-- Header -->
        <div class="copilot-header">
          <div class="copilot-header__left">
            <button class="copilot-btn copilot-btn--icon" id="copilotHistory" title="${t('copilot.history')}">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </button>
            <h3 class="copilot-title">${t('copilot.title')}</h3>
          </div>
          <div class="copilot-header__right">
            <button class="copilot-btn copilot-btn--icon" id="copilotNew" title="${t('copilot.newChat')}">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
            <button class="copilot-btn copilot-btn--icon" id="copilotClose" title="${t('common.close')}">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Messages Area -->
        <div class="copilot-messages" id="copilotMessages">
          ${renderMessagesContent()}
        </div>

        <!-- Input Area -->
        <div class="copilot-input-area">
          <textarea
            class="copilot-input"
            id="copilotInput"
            placeholder="${t('copilot.placeholder')}"
            rows="1"
          ></textarea>
          <button class="copilot-send" id="copilotSend" title="${t('copilot.send')}">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  `;

  // Cache DOM elements
  panelEl = container.querySelector('.copilot-panel');
  messagesEl = container.querySelector('#copilotMessages');
  inputEl = container.querySelector('#copilotInput');

  attachEventListeners();
}

/**
 * Render messages content
 */
function renderMessagesContent() {
  if (state.showHistory) {
    return renderHistoryContent();
  }

  if (state.messages.length === 0) {
    return renderWelcome();
  }

  return state.messages.map(msg => renderMessage(msg)).join('') +
    (state.isLoading ? renderTypingIndicator() : '');
}

/**
 * Render welcome screen
 */
function renderWelcome() {
  return `
    <div class="copilot-welcome">
      <div class="copilot-welcome__icon">
        <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          <circle cx="12" cy="10" r="1" fill="currentColor"/>
          <circle cx="8" cy="10" r="1" fill="currentColor"/>
          <circle cx="16" cy="10" r="1" fill="currentColor"/>
        </svg>
      </div>
      <h4 class="copilot-welcome__title">${t('copilot.title')}</h4>
      <p class="copilot-welcome__subtitle">${t('copilot.suggestions.title')}</p>
      <div class="copilot-suggestions">
        <button class="copilot-suggestion" data-suggestion="invoice">
          ${t('copilot.suggestions.invoice')}
        </button>
        <button class="copilot-suggestion" data-suggestion="contract">
          ${t('copilot.suggestions.contract')}
        </button>
        <button class="copilot-suggestion" data-suggestion="vendor">
          ${t('copilot.suggestions.vendor')}
        </button>
        <button class="copilot-suggestion" data-suggestion="discrepancy">
          ${t('copilot.suggestions.discrepancy')}
        </button>
      </div>
    </div>
  `;
}

/**
 * Render a single message
 */
function renderMessage(msg) {
  const isUser = msg.role === 'user';
  const isError = msg.isError;

  return `
    <div class="copilot-message copilot-message--${isUser ? 'user' : 'assistant'} ${isError ? 'copilot-message--error' : ''}">
      <div class="copilot-message__content">
        ${escapeHtml(msg.content)}
      </div>
      <div class="copilot-message__time">
        ${formatTime(msg.timestamp)}
      </div>
    </div>
  `;
}

/**
 * Render typing indicator
 */
function renderTypingIndicator() {
  return `
    <div class="copilot-typing">
      <span class="copilot-typing__dot"></span>
      <span class="copilot-typing__dot"></span>
      <span class="copilot-typing__dot"></span>
      <span class="copilot-typing__text">${t('copilot.typing')}</span>
    </div>
  `;
}

/**
 * Render history content
 */
function renderHistoryContent() {
  if (state.conversations.length === 0) {
    return `
      <div class="copilot-empty">
        <p>${t('copilot.noConversations')}</p>
      </div>
    `;
  }

  return `
    <div class="copilot-history">
      <div class="copilot-history__header">
        <button class="copilot-btn copilot-btn--back" id="copilotBackToChat">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          ${t('common.back')}
        </button>
      </div>
      <div class="copilot-history__list">
        ${state.conversations.map(conv => `
          <div class="copilot-history__item" data-conversation-id="${conv.id}">
            <div class="copilot-history__item-content">
              <span class="copilot-history__item-title">${escapeHtml(conv.title || 'Untitled')}</span>
              <span class="copilot-history__item-date">${formatDate(conv.lastMessageAt)}</span>
            </div>
            <button class="copilot-history__item-delete" data-delete-id="${conv.id}" title="${t('common.delete')}">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </button>
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

/**
 * Render messages only
 */
function renderMessages() {
  if (messagesEl) {
    messagesEl.innerHTML = renderMessagesContent();
    attachMessageEventListeners();
  }
}

/**
 * Render history only
 */
function renderHistory() {
  if (state.showHistory) {
    renderMessages();
  }
}

/**
 * Escape HTML
 */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Attach event listeners
 */
function attachEventListeners() {
  // Toggle button
  const toggleBtn = document.getElementById('copilotToggle');
  if (toggleBtn) {
    toggleBtn.addEventListener('click', toggle);
  }

  // Close button
  const closeBtn = document.getElementById('copilotClose');
  if (closeBtn) {
    closeBtn.addEventListener('click', close);
  }

  // New chat button
  const newBtn = document.getElementById('copilotNew');
  if (newBtn) {
    newBtn.addEventListener('click', startNewConversation);
  }

  // History button
  const historyBtn = document.getElementById('copilotHistory');
  if (historyBtn) {
    historyBtn.addEventListener('click', toggleHistory);
  }

  // Send button
  const sendBtn = document.getElementById('copilotSend');
  if (sendBtn) {
    sendBtn.addEventListener('click', sendMessage);
  }

  // Input
  if (inputEl) {
    inputEl.addEventListener('keypress', handleKeyPress);
    inputEl.addEventListener('input', handleInputChange);

    // Auto-resize textarea
    inputEl.addEventListener('input', () => {
      inputEl.style.height = 'auto';
      inputEl.style.height = Math.min(inputEl.scrollHeight, 120) + 'px';
    });
  }

  attachMessageEventListeners();
}

/**
 * Attach message-specific event listeners
 */
function attachMessageEventListeners() {
  // Suggestion buttons
  document.querySelectorAll('.copilot-suggestion').forEach(btn => {
    btn.addEventListener('click', () => {
      const suggestion = btn.textContent.trim();
      if (inputEl) {
        inputEl.value = suggestion;
        state.inputValue = suggestion;
        sendMessage();
      }
    });
  });

  // History items
  document.querySelectorAll('.copilot-history__item').forEach(item => {
    item.addEventListener('click', (e) => {
      if (!e.target.closest('.copilot-history__item-delete')) {
        const convId = item.dataset.conversationId;
        if (convId) loadConversation(convId);
      }
    });
  });

  // Delete buttons
  document.querySelectorAll('.copilot-history__item-delete').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const convId = btn.dataset.deleteId;
      if (convId) deleteConversation(convId);
    });
  });

  // Back to chat button
  const backBtn = document.getElementById('copilotBackToChat');
  if (backBtn) {
    backBtn.addEventListener('click', () => {
      state.showHistory = false;
      renderMessages();
    });
  }
}

/**
 * Inject styles
 */
function injectStyles() {
  if (document.getElementById('copilot-styles')) return;

  const style = document.createElement('style');
  style.id = 'copilot-styles';
  style.textContent = `
    /* Copilot Panel */
    .copilot-panel {
      position: fixed;
      top: 0;
      right: 0;
      height: 100vh;
      width: 0;
      background: var(--bg-primary, #fff);
      border-left: 1px solid var(--border-color, #e5e7eb);
      box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
      z-index: 999;
      display: flex;
      flex-direction: column;
      transition: width 0.3s ease;
      overflow: hidden;
    }

    .copilot-panel--open {
      width: 400px;
    }

    /* Toggle Button */
    .copilot-toggle {
      position: fixed;
      bottom: 24px;
      right: 24px;
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: var(--primary, #2563eb);
      color: white;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
      transition: transform 0.2s, box-shadow 0.2s;
      z-index: 1000;
    }

    .copilot-toggle:hover {
      transform: scale(1.05);
      box-shadow: 0 6px 16px rgba(37, 99, 235, 0.5);
    }

    .copilot-panel--open .copilot-toggle {
      display: none;
    }

    /* Content */
    .copilot-content {
      display: flex;
      flex-direction: column;
      height: 100%;
      opacity: 0;
      transition: opacity 0.2s ease 0.1s;
    }

    .copilot-panel--open .copilot-content {
      opacity: 1;
    }

    /* Header */
    .copilot-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px;
      border-bottom: 1px solid var(--border-color, #e5e7eb);
      background: var(--bg-secondary, #f9fafb);
    }

    .copilot-header__left,
    .copilot-header__right {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .copilot-title {
      font-size: 16px;
      font-weight: 600;
      margin: 0;
      color: var(--text-primary, #111827);
    }

    /* Buttons */
    .copilot-btn {
      background: none;
      border: none;
      cursor: pointer;
      padding: 8px;
      border-radius: 8px;
      color: var(--text-secondary, #6b7280);
      transition: background 0.2s, color 0.2s;
    }

    .copilot-btn:hover {
      background: var(--bg-tertiary, #f3f4f6);
      color: var(--text-primary, #111827);
    }

    .copilot-btn--back {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 14px;
    }

    /* Messages */
    .copilot-messages {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    /* Welcome */
    .copilot-welcome {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 32px 16px;
      height: 100%;
    }

    .copilot-welcome__icon {
      color: var(--primary, #2563eb);
      margin-bottom: 16px;
    }

    .copilot-welcome__title {
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 8px;
      color: var(--text-primary, #111827);
    }

    .copilot-welcome__subtitle {
      font-size: 14px;
      color: var(--text-secondary, #6b7280);
      margin: 0 0 24px;
    }

    /* Suggestions */
    .copilot-suggestions {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 100%;
      max-width: 300px;
    }

    .copilot-suggestion {
      background: var(--bg-secondary, #f9fafb);
      border: 1px solid var(--border-color, #e5e7eb);
      border-radius: 8px;
      padding: 12px 16px;
      font-size: 13px;
      color: var(--text-primary, #111827);
      cursor: pointer;
      text-align: left;
      transition: background 0.2s, border-color 0.2s;
    }

    .copilot-suggestion:hover {
      background: var(--bg-tertiary, #f3f4f6);
      border-color: var(--primary, #2563eb);
    }

    /* Message */
    .copilot-message {
      max-width: 85%;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .copilot-message--user {
      align-self: flex-end;
    }

    .copilot-message--assistant {
      align-self: flex-start;
    }

    .copilot-message__content {
      padding: 12px 16px;
      border-radius: 16px;
      font-size: 14px;
      line-height: 1.5;
      white-space: pre-wrap;
      word-wrap: break-word;
    }

    .copilot-message--user .copilot-message__content {
      background: var(--primary, #2563eb);
      color: white;
      border-bottom-right-radius: 4px;
    }

    .copilot-message--assistant .copilot-message__content {
      background: var(--bg-secondary, #f9fafb);
      color: var(--text-primary, #111827);
      border-bottom-left-radius: 4px;
    }

    .copilot-message--error .copilot-message__content {
      background: var(--error-light, #fee2e2);
      color: var(--error, #dc2626);
    }

    .copilot-message__time {
      font-size: 11px;
      color: var(--text-tertiary, #9ca3af);
      padding: 0 4px;
    }

    .copilot-message--user .copilot-message__time {
      text-align: right;
    }

    /* Typing Indicator */
    .copilot-typing {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      background: var(--bg-secondary, #f9fafb);
      border-radius: 16px;
      border-bottom-left-radius: 4px;
      align-self: flex-start;
    }

    .copilot-typing__dot {
      width: 8px;
      height: 8px;
      background: var(--text-tertiary, #9ca3af);
      border-radius: 50%;
      animation: typing-bounce 1.4s infinite ease-in-out both;
    }

    .copilot-typing__dot:nth-child(1) { animation-delay: -0.32s; }
    .copilot-typing__dot:nth-child(2) { animation-delay: -0.16s; }
    .copilot-typing__dot:nth-child(3) { animation-delay: 0s; }

    @keyframes typing-bounce {
      0%, 80%, 100% { transform: scale(0.6); }
      40% { transform: scale(1); }
    }

    .copilot-typing__text {
      font-size: 13px;
      color: var(--text-secondary, #6b7280);
    }

    /* Input Area */
    .copilot-input-area {
      display: flex;
      align-items: flex-end;
      gap: 8px;
      padding: 16px;
      border-top: 1px solid var(--border-color, #e5e7eb);
      background: var(--bg-primary, #fff);
    }

    .copilot-input {
      flex: 1;
      padding: 12px 16px;
      border: 1px solid var(--border-color, #e5e7eb);
      border-radius: 12px;
      font-size: 14px;
      resize: none;
      min-height: 44px;
      max-height: 120px;
      line-height: 1.4;
      font-family: inherit;
    }

    .copilot-input:focus {
      outline: none;
      border-color: var(--primary, #2563eb);
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }

    .copilot-send {
      width: 44px;
      height: 44px;
      border: none;
      border-radius: 12px;
      background: var(--primary, #2563eb);
      color: white;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s;
      flex-shrink: 0;
    }

    .copilot-send:hover {
      background: var(--primary-dark, #1d4ed8);
    }

    .copilot-send:disabled {
      background: var(--gray-300, #d1d5db);
      cursor: not-allowed;
    }

    /* History */
    .copilot-history {
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    .copilot-history__header {
      padding: 0 0 12px;
      border-bottom: 1px solid var(--border-color, #e5e7eb);
      margin-bottom: 12px;
    }

    .copilot-history__list {
      flex: 1;
      overflow-y: auto;
    }

    .copilot-history__item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px;
      border-radius: 8px;
      cursor: pointer;
      transition: background 0.2s;
    }

    .copilot-history__item:hover {
      background: var(--bg-secondary, #f9fafb);
    }

    .copilot-history__item-content {
      flex: 1;
      min-width: 0;
    }

    .copilot-history__item-title {
      display: block;
      font-size: 14px;
      font-weight: 500;
      color: var(--text-primary, #111827);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .copilot-history__item-date {
      display: block;
      font-size: 12px;
      color: var(--text-tertiary, #9ca3af);
      margin-top: 2px;
    }

    .copilot-history__item-delete {
      background: none;
      border: none;
      padding: 8px;
      cursor: pointer;
      color: var(--text-tertiary, #9ca3af);
      border-radius: 4px;
      transition: background 0.2s, color 0.2s;
      opacity: 0;
    }

    .copilot-history__item:hover .copilot-history__item-delete {
      opacity: 1;
    }

    .copilot-history__item-delete:hover {
      background: var(--error-light, #fee2e2);
      color: var(--error, #dc2626);
    }

    /* Empty State */
    .copilot-empty {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: var(--text-tertiary, #9ca3af);
      font-size: 14px;
    }

    /* Responsive */
    @media (max-width: 480px) {
      .copilot-panel--open {
        width: 100%;
      }

      .copilot-toggle {
        bottom: 16px;
        right: 16px;
        width: 48px;
        height: 48px;
      }
    }

    /* RTL Support */
    [dir="rtl"] .copilot-panel {
      right: auto;
      left: 0;
      border-left: none;
      border-right: 1px solid var(--border-color, #e5e7eb);
      box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);
    }

    [dir="rtl"] .copilot-toggle {
      right: auto;
      left: 24px;
    }

    [dir="rtl"] .copilot-message--user {
      align-self: flex-start;
    }

    [dir="rtl"] .copilot-message--assistant {
      align-self: flex-end;
    }

    [dir="rtl"] .copilot-message--user .copilot-message__content {
      border-bottom-right-radius: 16px;
      border-bottom-left-radius: 4px;
    }

    [dir="rtl"] .copilot-message--assistant .copilot-message__content {
      border-bottom-left-radius: 16px;
      border-bottom-right-radius: 4px;
    }
  `;

  document.head.appendChild(style);
}

export default {
  init,
  toggle,
  open,
  close,
};
